import React, { useState, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckIcon } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { ErrorModal } from '../components/ErrorModal';
export const BounceHousePackages = () => {
  const {
    addToCart
  } = useCart();
  const navigate = useNavigate();
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());
  const [errorMessage, setErrorMessage] = useState<string>('');
  const handleAddToCart = (pkg: (typeof packages)[0]) => {
    const result = addToCart({
      type: 'bouncehouse',
      packageName: pkg.name,
      packageValue: pkg.packageValue,
      price: pkg.price
    });
    if (result.success) {
      // Show success feedback briefly, then redirect to cart
      setAddedItems(prev => new Set(prev).add(pkg.packageValue));
      setErrorMessage('');
      // Redirect to cart after 800ms
      setTimeout(() => {
        navigate('/cart');
        window.scrollTo(0, 0);
      }, 800);
    } else {
      // Show error modal (no scroll)
      setErrorMessage(result.message || 'Unable to add item to cart');
    }
  };
  const packages = [{
    id: '01',
    name: 'Colorful Combo Bounce House',
    packageValue: 'combo-bounce-house',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gHRpHpQ744Tqwj57J7go3N/image.png',
    description: 'A larger option, this vibrant bounce house features a spacious play area filled with multicolored balls. Ideal for bigger groups of little ones, offering endless fun and laughter.',
    dimensions: '9ft W x 13ft L x 7ft H',
    features: ['Combo design with ball pit and slide', 'Vibrant, colorful appearance', 'Larger size for more play space', 'Multicolored balls included', 'Perfect for bigger events'],
    price: '$150'
  }, {
    id: '02',
    name: 'Medium White Bounce House',
    packageValue: 'medium-white-bounce-house',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vnJ8Vsi2yVGyEnBLbZNqzj/image.png',
    description: "With it's soft white design it provides an upscale experience while ensuring plenty of space for toddlers to jump, play, and enjoy. Perfect for those looking to add a touch of luxury to their event.",
    dimensions: '8ft W x 10ft L x 8ft H',
    features: ['Elegant all-white design', 'Medium size for versatile placement', 'Clean, modern aesthetic', 'Perfect for upscale events', 'Complements any party décor'],
    price: '$200'
  }, {
    id: '03',
    name: 'White Combo Bounce House',
    packageValue: 'mega-white-combo-bounce-house',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/42dN8ZAPRjx3pURCvvrteu/image.png',
    description: 'Our largest and most impressive bounce house! This stunning premium white castle design features elegant towers and spacious interior, perfect for creating an unforgettable luxury experience for your little ones.',
    dimensions: '13ft W x 15ft L x 10ft H',
    features: ['Largest bounce house in our collection', 'Premium white castle design with towers', 'Spacious interior for maximum play', 'Elegant and sophisticated appearance', 'Perfect for upscale celebrations', 'Accommodates larger groups'],
    price: '$225'
  }, {
    id: '04',
    name: 'White Combo Bounce House w/ Ball Pit',
    packageValue: 'combo-white-bounce-house',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rDecq4r6w9CqRJodkYNa3L/0C7B1578-7567-45F8-8CC7-AC72C34BAC1E-768x1024.jpg',
    description: 'Featuring a premium white design, large slide, and ball pit, this bounce house provides immense fun for your adventurous toddlers, while keeping the party classy!',
    dimensions: '13ft W x 13ft L x 10ft H',
    features: ['Premium white design', 'Includes ball pit and curved slide', 'Perfect for luxury events', 'Accommodates multiple children'],
    price: '$250'
  }];
  return <>
      <ErrorModal isOpen={!!errorMessage} message={errorMessage} onClose={() => setErrorMessage('')} data-id="element-664" />

      <div className="bg-white w-full" data-id="element-665">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-666">
          {/* Page Title Section */}
          <div className="text-center mb-16" data-id="element-667">
            <h1 className="text-4xl md:text-5xl font-bold text-primary-500 leading-tight" data-id="element-668">
              Discover Our Fleet Of Bounce Houses
            </h1>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-669">
              We offer a variety of bounce houses designed to bring your vision
              to life and create memorable, one-of-a-kind experiences for little
              ones!
            </p>
          </div>

          {/* Package Cards Section */}
          <div className="space-y-8" data-id="element-670">
            {packages.map(pkg => <div key={pkg.id} className="bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-100" data-id="element-671">
                <div className="flex flex-col md:flex-row" data-id="element-672">
                  {/* Package Image */}
                  <div className="md:w-2/5" data-id="element-673">
                    <img src={pkg.image} alt={`${pkg.name}`} className={`w-full h-full ${pkg.id === '03' ? 'object-cover object-center' : 'object-contain bg-gray-50'}`} data-id="element-674" />
                  </div>
                  {/* Package Details */}
                  <div className="p-8 md:w-3/5 flex flex-col h-full" data-id="element-675">
                    <div className="flex-grow" data-id="element-676">
                      <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-677">
                        <span className="text-primary-500" data-id="element-678">{pkg.id}</span> -{' '}
                        {pkg.name}
                      </h2>
                      <p className="text-sm font-medium text-gray-500 mb-4" data-id="element-679">
                        📐 {pkg.dimensions}
                      </p>
                      <p className="text-gray-600 mb-6" data-id="element-680">{pkg.description}</p>
                      <ul className="space-y-3" data-id="element-681">
                        {pkg.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-682">
                            <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-683" />
                            <span data-id="element-684">{feature}</span>
                          </li>)}
                      </ul>
                    </div>
                    <div className="mt-auto pt-6 text-center border-t border-gray-100" data-id="element-685">
                      <span className="text-4xl font-bold text-green-800 block mb-4" data-id="element-686">
                        {pkg.price}
                      </span>
                      <button onClick={() => handleAddToCart(pkg)} className={`inline-flex items-center px-6 py-3 rounded-md font-medium transition-all shadow-md ${addedItems.has(pkg.packageValue) ? 'bg-green-500 text-white' : 'bg-secondary-500 text-white hover:bg-secondary-600'}`} data-id="element-687">
                        {addedItems.has(pkg.packageValue) ? '✓ Added to Cart' : 'Add to Cart'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </>;
};